/**
 * CPALE Knowledge Base v2.0
 * 
 * Comprehensive, verified knowledge for Philippine CPA Licensure Exam
 * 
 * Improvements:
 * - Verified citations only (no hallucinations)
 * - Updated for 2024-2025 CPALE syllabus
 * - Structured for efficient retrieval
 * - Cross-referenced standards and laws
 * - Common exam traps documented
 */

// ==================== Types ====================

export interface KnowledgeEntry {
    topic: string;
    subtopic: string;
    standard?: string;           // PFRS/PAS/PSA number
    section?: string;            // Specific paragraph
    law?: string;                // RA/NIRC section
    content: string;
    keyTerms: string[];
    commonTraps?: string[];
    examTips?: string[];
    relatedTopics?: string[];
    lastVerified: string;        // YYYY-MM-DD
}

export interface StandardReference {
    code: string;               // e.g., "PAS 16"
    title: string;
    effectiveDate: string;
    keyParagraphs: Record<string, string>;  // paragraph -> summary
    amendments?: string[];
}

export interface TaxRate {
    type: string;
    rate: string;
    basis: string;
    conditions?: string;
    effectiveDate: string;
    source: string;             // NIRC section or RA
}

// ==================== FINANCIAL ACCOUNTING (FAR) ====================

export const FAR_KNOWLEDGE: KnowledgeEntry[] = [
    // Conceptual Framework
    {
        topic: "FAR",
        subtopic: "Conceptual Framework 2018",
        standard: "CF",
        content: `The Conceptual Framework for Financial Reporting (2018) provides the foundation for PFRS/PAS. Key definitions:
- **Asset**: Present economic resource controlled by entity as result of past events
- **Liability**: Present obligation to transfer economic resource as result of past events
- **Equity**: Residual interest in assets after deducting liabilities
- **Income**: Increases in assets/decreases in liabilities resulting in increases in equity (excluding contributions from owners)
- **Expense**: Decreases in assets/increases in liabilities resulting in decreases in equity (excluding distributions to owners)`,
        keyTerms: ["economic resource", "present obligation", "control", "past events", "residual interest"],
        commonTraps: [
            "Asset definition now uses 'economic resource' not 'economic benefits'",
            "Control is essential - legal ownership alone is NOT sufficient",
            "Liability requires PRESENT obligation, not future commitments"
        ],
        examTips: [
            "Focus on 'present' and 'past' timing - future events don't create assets/liabilities",
            "Remember: CONTROL, not ownership, defines an asset"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 2 - Inventories
    {
        topic: "FAR",
        subtopic: "Inventories",
        standard: "PAS 2",
        content: `**PAS 2 Inventories** - Measurement and Costing

**Initial Measurement**: Cost includes:
- Purchase price (less discounts, rebates)
- Import duties and non-recoverable taxes
- Transport, handling directly attributable
- Other costs to bring to present location/condition

**Cost Formulas**:
- FIFO (First-In, First-Out)
- Weighted Average Cost
- Specific Identification (for items not ordinarily interchangeable)
- **LIFO is PROHIBITED under PFRS**

**Subsequent Measurement**: Lower of Cost and Net Realizable Value (LCNRV)
- NRV = Estimated selling price - Estimated costs to complete - Estimated costs to sell
- Write-down recognized in COGS (P&L)
- Reversal of write-down allowed if NRV increases (limited to original cost)`,
        keyTerms: ["LCNRV", "NRV", "FIFO", "Weighted Average", "write-down reversal"],
        commonTraps: [
            "NRV is NOT the same as Fair Value",
            "Storage costs are NOT included unless necessary for production process",
            "Administrative overhead is EXCLUDED from inventory cost",
            "LIFO is PROHIBITED - this is a common MCQ trap"
        ],
        examTips: [
            "Always compare Cost vs NRV for each item or category",
            "Write-down goes to COGS, not a separate expense line",
            "Reversal is limited - cannot exceed original cost"
        ],
        relatedTopics: ["PAS 36 Impairment", "PAS 16 PPE"],
        lastVerified: "2024-12-01"
    },

    // PAS 16 - PPE
    {
        topic: "FAR",
        subtopic: "Property, Plant and Equipment",
        standard: "PAS 16",
        content: `**PAS 16 Property, Plant and Equipment**

**Recognition**: When probable future economic benefits AND cost reliably measurable

**Initial Measurement - Cost includes**:
- Purchase price (less discounts)
- Directly attributable costs to bring to working condition
- Dismantling/restoration costs (PAS 37 provision)
- Borrowing costs if qualifying asset (PAS 23)

**EXCLUDES from cost**:
- Abnormal waste
- Administrative/general overhead
- Initial operating losses
- Staff training costs

**Subsequent Measurement (Accounting Policy Choice)**:
1. **Cost Model**: Cost - Accumulated Depreciation - Impairment
2. **Revaluation Model**: Fair Value - Subsequent Depreciation - Impairment
   - Must revalue regularly (3-5 years typical)
   - Entire class must use same model
   - Revaluation surplus → OCI (Equity)
   - Revaluation decrease → P&L (unless reversing prior surplus)

**Depreciation**:
- Depreciable amount = Cost - Residual Value
- Methods: Straight-line, Diminishing balance, Units of production
- Component depreciation required if significant parts have different lives
- Review residual value and useful life at least annually`,
        keyTerms: ["directly attributable", "revaluation surplus", "component depreciation", "residual value"],
        commonTraps: [
            "Training costs are NEVER capitalized",
            "Revaluation surplus goes to OCI, not P&L",
            "Only DIRECTLY attributable costs are capitalized",
            "Component depreciation is REQUIRED, not optional"
        ],
        examTips: [
            "Always check if cost includes installation, testing, dismantling",
            "For revaluation: increase → OCI, decrease → P&L (unless reversing)",
            "Review periods for residual value and useful life reviews"
        ],
        relatedTopics: ["PAS 36 Impairment", "PAS 23 Borrowing Costs", "PAS 37 Provisions"],
        lastVerified: "2024-12-01"
    },

    // PFRS 15 - Revenue
    {
        topic: "FAR",
        subtopic: "Revenue from Contracts with Customers",
        standard: "PFRS 15",
        content: `**PFRS 15 Revenue from Contracts with Customers**

**5-Step Model**:

**Step 1: Identify Contract(s)**
- Approved by parties, rights identifiable, payment terms, commercial substance, collectibility probable

**Step 2: Identify Performance Obligations**
- Distinct goods/services (capable of being distinct + distinct in context)
- Series of distinct goods/services that are substantially same

**Step 3: Determine Transaction Price**
- Includes: Fixed, variable (constrained), financing component, non-cash, payable to customer
- Variable consideration: Use expected value OR most likely amount
- Significant financing: Adjust for time value if > 1 year

**Step 4: Allocate Transaction Price**
- Based on standalone selling prices
- Methods: Adjusted market, Expected cost plus margin, Residual (limited use)

**Step 5: Recognize Revenue**
- **Over time** (any ONE of these):
  1. Customer simultaneously receives and consumes benefits
  2. Entity creates/enhances customer-controlled asset
  3. No alternative use + enforceable right to payment for performance to date
- **Point in time**: When control transfers (indicators in PFRS 15.38)`,
        keyTerms: ["performance obligation", "distinct", "over time", "point in time", "variable consideration", "control"],
        commonTraps: [
            "Financing component ignored if < 1 year",
            "'Over time' requires meeting at least ONE criterion",
            "Variable consideration must be 'constrained' (highly probable no reversal)",
            "Bill-and-hold arrangements require specific criteria"
        ],
        examTips: [
            "Memorize the 3 criteria for 'over time' recognition",
            "Focus on CONTROL transfer, not risks and rewards (old IAS 18)",
            "Contract modifications: treated as separate OR cumulative catch-up"
        ],
        relatedTopics: ["PFRS 16 Leases", "PAS 20 Government Grants"],
        lastVerified: "2024-12-01"
    },

    // PFRS 9 - Financial Instruments
    {
        topic: "FAR",
        subtopic: "Financial Instruments - Classification and Measurement",
        standard: "PFRS 9",
        content: `**PFRS 9 Financial Instruments**

**Classification of Financial Assets** (based on business model + cash flow characteristics):

1. **Amortized Cost**
   - Hold to collect contractual cash flows
   - Cash flows are SPPI (Solely Payments of Principal and Interest)
   - Example: Term loans, trade receivables

2. **FVOCI (Fair Value through OCI)**
   - Hold to collect AND sell
   - Cash flows are SPPI
   - Example: Debt securities held for trading and collection
   - **Equity instruments**: Irrevocable election at initial recognition

3. **FVTPL (Fair Value through Profit or Loss)**
   - All other financial assets
   - Trading intent or fails SPPI test
   - Example: Derivatives, equity investments (unless FVOCI elected)

**Expected Credit Loss (ECL) Model**:
- **Stage 1** (12-month ECL): No significant increase in credit risk since initial recognition
- **Stage 2** (Lifetime ECL): Significant increase in credit risk
- **Stage 3** (Lifetime ECL): Credit-impaired
- **Simplified approach**: Lifetime ECL from day 1 (trade receivables, lease receivables)`,
        keyTerms: ["SPPI", "amortized cost", "FVOCI", "FVTPL", "ECL", "lifetime ECL", "12-month ECL"],
        commonTraps: [
            "FVOCI for equity is IRREVOCABLE election",
            "Dividends from FVOCI equity → P&L, not OCI",
            "Trade receivables typically use simplified approach (lifetime ECL from start)",
            "Reclassification only when business model CHANGES (rare)"
        ],
        examTips: [
            "Focus on SPPI test and business model for classification",
            "Remember the 3-stage ECL model and triggers for stage movement",
            "Simplified approach = no Stage 1, always lifetime ECL"
        ],
        relatedTopics: ["PFRS 7 Disclosures", "PAS 32 Presentation"],
        lastVerified: "2024-12-01"
    },

    // PFRS 16 - Leases
    {
        topic: "FAR",
        subtopic: "Leases",
        standard: "PFRS 16",
        content: `**PFRS 16 Leases**

**Definition**: Contract that conveys right to control use of identified asset for a period of time in exchange for consideration

**Lessee Accounting (Single Model)**:
- Recognize Right-of-Use Asset and Lease Liability for ALL leases
- **Exceptions**: Short-term leases (≤12 months) and low-value assets (US$5,000 threshold)

**Initial Measurement**:
- **ROU Asset** = Lease liability + Initial direct costs + Prepaid lease payments + Restoration costs - Lease incentives received
- **Lease Liability** = PV of lease payments (using implicit rate or IBR)

**Lease Payments include**:
- Fixed payments (less incentives)
- Variable payments based on index/rate
- Residual value guarantees (expected to be paid)
- Purchase option price (if reasonably certain)
- Termination penalties (if lease term reflects termination)

**Subsequent Measurement**:
- ROU Asset: Cost model (or Revaluation/Fair Value for investment property)
- Lease Liability: Effective interest method

**Lessor Accounting (Dual Model - unchanged from IAS 17)**:
- Finance Lease: Transfer substantially all risks and rewards
- Operating Lease: All others`,
        keyTerms: ["ROU asset", "lease liability", "IBR", "implicit rate", "short-term lease", "low-value asset"],
        commonTraps: [
            "Variable payments NOT based on index/rate are NOT included in liability",
            "Initial direct costs go to ROU asset, not expense",
            "Short-term exemption is by CLASS of asset, not individual lease",
            "Lease modification may require remeasurement OR separate lease"
        ],
        examTips: [
            "Always calculate both ROU asset and liability separately - they start DIFFERENT",
            "Know the formula: ROU = Liability + Initial Direct + Prepaid - Incentives + Restoration",
            "Remember: Lessee uses single model, Lessor uses dual model"
        ],
        relatedTopics: ["PAS 40 Investment Property", "PAS 36 Impairment"],
        lastVerified: "2024-12-01"
    },
    // PAS 1 - Presentation of FS
    {
        topic: "FAR",
        subtopic: "Presentation of Financial Statements",
        standard: "PAS 1",
        content: `**PAS 1 Presentation of Financial Statements**
        
**Complete Set of FS**:
1. Statement of Financial Position
2. Statement of Profit or Loss and OCI
3. Statement of Changes in Equity
4. Statement of Cash Flows
5. Notes
6. Statement of Financial Position at beginning of preceding period (if retrospective application/restatement)

**Key Principles**:
- Fair presentation and compliance with PFRS
- Going concern assumption
- Accrual basis (except Cash Flow)
- Materiality and aggregation
- Offsetting (generally prohibited unless required/permitted)
- Frequency of reporting (at least annually)
- Comparative information`,
        keyTerms: ["complete set", "going concern", "accrual", "materiality", "offsetting"],
        commonTraps: [
            "Offsetting is generally PROHIBITED",
            "Going concern is the ONLY underlying assumption",
            "OCI items are recycled or not recycled - know which is which"
        ],
        examTips: [
            "Memorize the components of a complete set of FS",
            "Know the difference between Current and Non-current classification"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 7 - Cash Flows
    {
        topic: "FAR",
        subtopic: "Statement of Cash Flows",
        standard: "PAS 7",
        content: `**PAS 7 Statement of Cash Flows**

**Classifications**:
1. **Operating**: Principal revenue-producing activities (e.g., cash from customers, payments to suppliers)
2. **Investing**: Acquisition/disposal of long-term assets and investments (e.g., purchase of PPE, loans to others)
3. **Financing**: Changes in equity and borrowings (e.g., issue of shares, bank loans, dividends paid)

**Interest and Dividends**:
- Interest Paid: Operating OR Financing
- Interest Received: Operating OR Investing
- Dividends Paid: Financing OR Operating
- Dividends Received: Operating OR Investing
*Must be consistent from period to period*`,
        keyTerms: ["operating", "investing", "financing", "direct method", "indirect method"],
        commonTraps: [
            "Bank overdrafts repayable on demand are part of Cash & Cash Equivalents",
            "Non-cash transactions (e.g., acquiring asset by assuming debt) are EXCLUDED from the body of the statement (disclosed in notes)"
        ],
        examTips: [
            "Master the Indirect Method for Operating Activities (Net Income + Non-cash exp - Non-cash inc +/- Working Capital changes)",
            "Investing = Non-current Assets; Financing = Non-current Liabilities & Equity"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 8 - Accounting Policies
    {
        topic: "FAR",
        subtopic: "Accounting Policies, Changes in Estimates and Errors",
        standard: "PAS 8",
        content: `**PAS 8 Policies, Estimates, Errors**

**Accounting Policy Change**:
- Retrospective application (adjust opening balance of earliest period presented)
- Example: FIFO to Weighted Average, Cost to Fair Value model

**Change in Accounting Estimate**:
- Prospective application (current and future periods only)
- Example: Change in useful life, salvage value, bad debt %

**Prior Period Errors**:
- Retrospective restatement (correct prior period FS)`,
        keyTerms: ["retrospective", "prospective", "policy", "estimate", "error"],
        commonTraps: [
            "Change in depreciation method is a Change in ESTIMATE (prospective), not policy",
            "Inventory cost formula change is a Change in POLICY (retrospective)"
        ],
        examTips: [
            "If difficult to distinguish between policy and estimate, treat as Change in ESTIMATE",
            "Errors are always retrospective"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 10 - Events After Reporting Period
    {
        topic: "FAR",
        subtopic: "Events After the Reporting Period",
        standard: "PAS 10",
        content: `**PAS 10 Events After Reporting Period**

**Adjusting Events**:
- Provide evidence of conditions that existed AT the reporting date
- Require adjustment of FS
- Example: Settlement of court case confirming liability, bankruptcy of customer confirming bad debt

**Non-adjusting Events**:
- Indicative of conditions that arose AFTER the reporting date
- Disclose only (if material)
- Example: Major fire after year-end, decline in market value of investments`,
        keyTerms: ["adjusting", "non-adjusting", "reporting date", "authorization for issue"],
        commonTraps: [
            "Dividends declared after reporting date are NON-ADJUSTING (not a liability at year-end)",
            "Bankruptcy of customer usually confirms condition at year-end (Adjusting)"
        ],
        examTips: [
            "Ask: Did the condition exist at Dec 31?",
            "Yes = Adjust; No = Disclose"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 12 - Income Taxes
    {
        topic: "FAR",
        subtopic: "Income Taxes",
        standard: "PAS 12",
        content: `**PAS 12 Income Taxes**

**Current Tax**: Tax payable/recoverable for current period

**Deferred Tax**:
- **Temporary Differences**: Differences between Carrying Amount (CA) and Tax Base (TB)
- **Deferred Tax Liability (DTL)**: Taxable temporary difference (CA > TB for Asset; CA < TB for Liability) -> "Will pay more tax later"
- **Deferred Tax Asset (DTA)**: Deductible temporary difference (CA < TB for Asset; CA > TB for Liability) -> "Will pay less tax later"

**Measurement**: Use tax rates enacted or substantively enacted at reporting date`,
        keyTerms: ["temporary difference", "permanent difference", "DTA", "DTL", "tax base"],
        commonTraps: [
            "Permanent differences (e.g., fines, exempt income) do NOT create DTA/DTL",
            "Revaluation of asset usually creates DTL (CA increases, TB stays same)"
        ],
        examTips: [
            "Asset: CA > TB = DTL (Taxable)",
            "Liability: CA > TB = DTA (Deductible)"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 19 - Employee Benefits
    {
        topic: "FAR",
        subtopic: "Employee Benefits",
        standard: "PAS 19",
        content: `**PAS 19 Employee Benefits**

**Short-term benefits**: Recognized as expense when service rendered (undiscounted)

**Post-employment benefits**:
1. **Defined Contribution**: Expense = Contribution payable
2. **Defined Benefit**:
   - **Deficit/Surplus** = PV of DBO - FV of Plan Assets
   - **Service Cost** (Current/Past/Gain-Loss on Settlement) -> P&L
   - **Net Interest** (Net Liability x Discount Rate) -> P&L
   - **Remeasurements** (Actuarial gains/losses, Return on assets less interest) -> OCI (Not recycled)`,
        keyTerms: ["defined benefit", "defined contribution", "actuarial gain/loss", "DBO", "plan assets", "corridor approach (abolished)"],
        commonTraps: [
            "Actuarial gains/losses go to OCI, NEVER recycled to P&L",
            "Past service cost is recognized IMMEDIATELY in P&L",
            "Asset ceiling applies to Surplus"
        ],
        examTips: [
            "Memorize the 3 components: Service Cost (P&L), Net Interest (P&L), Remeasurements (OCI)",
            "Discount rate based on high quality corporate bonds"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 23 - Borrowing Costs
    {
        topic: "FAR",
        subtopic: "Borrowing Costs",
        standard: "PAS 23",
        content: `**PAS 23 Borrowing Costs**

**Core Principle**: Borrowing costs directly attributable to acquisition/construction of **Qualifying Asset** must be CAPITALIZED.

**Qualifying Asset**: Takes substantial time to get ready (e.g., manufacturing plant, power generation facility).
*Not qualifying: Inventories produced in short period, assets ready for use.*

**Capitalization**:
- **Specific Borrowing**: Actual costs less investment income
- **General Borrowing**: Weighted Average Capitalization Rate x Expenditures
- **Suspension**: During extended periods of suspension of active development
- **Cessation**: When substantially all activities are complete`,
        keyTerms: ["qualifying asset", "capitalization", "specific borrowing", "general borrowing"],
        commonTraps: [
            "Investment income from SPECIFIC borrowings reduces capitalizable cost",
            "Investment income from GENERAL borrowings is IGNORED (interest income)",
            "Land is not qualifying unless undergoing development"
        ],
        examTips: [
            "Calculate Average Accumulated Expenditures first",
            "Compare AAE with Specific Borrowing to determine General Borrowing portion"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 24 - Related Party Disclosures
    {
        topic: "FAR",
        subtopic: "Related Party Disclosures",
        standard: "PAS 24",
        content: `**PAS 24 Related Party Disclosures**

**Related Parties**:
- Control / Joint Control / Significant Influence
- Key Management Personnel (KMP)
- Close family members of the above
- Associates / Joint Ventures

**Disclosures**:
- Nature of relationship
- Transactions (amount, terms)
- Outstanding balances (provisions for bad debts)
- KMP compensation (total and by category)`,
        keyTerms: ["related party", "KMP", "close family member", "control", "significant influence"],
        commonTraps: [
            "Two entities with a common director are NOT necessarily related (unless director has control/joint control)",
            "Providers of finance/unions/utilities are NOT related parties simply by virtue of dealings"
        ],
        examTips: [
            "Focus on WHO is related (Parent, Sub, Associate, KMP)",
            "Disclosure only standard - no measurement impact"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 28 - Investments in Associates
    {
        topic: "FAR",
        subtopic: "Investments in Associates and Joint Ventures",
        standard: "PAS 28",
        content: `**PAS 28 Investments in Associates**

**Significant Influence**: Power to participate in financial/operating policy decisions (usually 20%-50% ownership).

**Equity Method**:
- Initial: At Cost
- Subsequent:
  - Add: Share of Profit (P&L) and OCI
  - Deduct: Dividends received
  - Adjust: Amortization of undervaluation of assets
  - Deduct: Impairment losses

**Intercompany Transactions**:
- Eliminate upstream and downstream profits to extent of investor's interest`,
        keyTerms: ["significant influence", "equity method", "associate", "joint venture"],
        commonTraps: [
            "Dividends received reduce the Investment account, NOT income",
            "Share in Net Income increases Investment account",
            "If influence lost -> Switch to PFRS 9 (Fair Value)"
        ],
        examTips: [
            "Master the T-account for Investment in Associate",
            "Don't forget amortization of excess fair value"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 33 - Earnings Per Share
    {
        topic: "FAR",
        subtopic: "Earnings Per Share",
        standard: "PAS 33",
        content: `**PAS 33 Earnings Per Share**

**Basic EPS**:
(Net Income - Preferred Dividends) / Weighted Average Outstanding Ordinary Shares

**Diluted EPS**:
- "What if" potential ordinary shares were converted?
- **Convertible Bonds**: Add back interest (net of tax) to numerator; Add shares to denominator
- **Options/Warrants**: Treasury Share Method (assume exercise and buyback at average market price)
- **Antidilutive**: If Diluted EPS > Basic EPS, ignore (report Basic only)`,
        keyTerms: ["basic EPS", "diluted EPS", "weighted average", "potential ordinary shares", "antidilutive"],
        commonTraps: [
            "Cumulative Pref Div: Deduct whether declared or not",
            "Non-cumulative Pref Div: Deduct only if declared",
            "Options are dilutive only if Exercise Price < Average Market Price"
        ],
        examTips: [
            "Always calculate Basic EPS first",
            "Check for antidilution individually and in sequence"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 36 - Impairment of Assets
    {
        topic: "FAR",
        subtopic: "Impairment of Assets",
        standard: "PAS 36",
        content: `**PAS 36 Impairment of Assets**

**Core Principle**: Asset should not be carried at more than its Recoverable Amount.

**Recoverable Amount**: HIGHER of:
1. Fair Value less Costs of Disposal (FVLCD)
2. Value in Use (VIU) - PV of future cash flows

**Impairment Loss**: Carrying Amount - Recoverable Amount
- Recognize in P&L (unless revalued asset -> OCI)

**Reversal**:
- Allowed for all assets EXCEPT Goodwill
- Limited to carrying amount if no impairment had been recognized`,
        keyTerms: ["impairment", "recoverable amount", "FVLCD", "VIU", "cash generating unit", "goodwill"],
        commonTraps: [
            "Goodwill impairment is NEVER reversed",
            "Recoverable amount is the HIGHER of the two",
            "Impairment of CGU: Allocate to Goodwill first, then pro-rata to other assets"
        ],
        examTips: [
            "Calculate CA, FVLCD, VIU. Compare CA vs Higher of (FVLCD, VIU)",
            "Order of allocation for CGU is critical"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 37 - Provisions
    {
        topic: "FAR",
        subtopic: "Provisions, Contingent Liabilities and Contingent Assets",
        standard: "PAS 37",
        content: `**PAS 37 Provisions**

**Recognition Criteria** (All 3 must be met):
1. Present obligation (legal or constructive) from past event
2. Probable outflow of resources (>50%)
3. Reliable estimate

**Contingent Liability**:
- Possible obligation OR Present obligation but not probable/reliable
- DISCLOSE only (unless remote -> ignore)

**Contingent Asset**:
- Probable -> Disclose
- Virtually Certain -> Recognize as Asset`,
        keyTerms: ["provision", "contingent liability", "constructive obligation", "probable", "remote", "virtually certain"],
        commonTraps: [
            "Constructive obligation (valid expectation created) is enough for provision",
            "Future operating losses -> NO provision",
            "Reimbursements (e.g., insurance) -> Separate asset (not netted), only if virtually certain"
        ],
        examTips: [
            "Probable (>50%) = Provision",
            "Possible (<50%) = Disclosure",
            "Remote = Do nothing"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 38 - Intangible Assets
    {
        topic: "FAR",
        subtopic: "Intangible Assets",
        standard: "PAS 38",
        content: `**PAS 38 Intangible Assets**

**Definition**: Identifiable non-monetary asset without physical substance.

**Recognition**:
- Probable economic benefits + Cost reliably measurable
- **Internally Generated Goodwill**: NEVER recognized
- **Research Phase**: Expense
- **Development Phase**: Capitalize if all criteria met (PIRATE - Probable benefits, Intention, Resources, Ability, Technical feasibility, Expenditure measurable)

**Measurement**:
- Finite life: Amortize
- Indefinite life: No amortization, test for impairment annually`,
        keyTerms: ["identifiable", "research", "development", "amortization", "indefinite life"],
        commonTraps: [
            "Start-up costs, training, advertising -> EXPENSE",
            "Internally generated brands/mastheads -> EXPENSE",
            "Revaluation model allowed but rare (requires active market)"
        ],
        examTips: [
            "Distinguish Research (Expense) vs Development (Capitalize)",
            "Indefinite life = No Amortization + Annual Impairment Test"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 40 - Investment Property
    {
        topic: "FAR",
        subtopic: "Investment Property",
        standard: "PAS 40",
        content: `**PAS 40 Investment Property**

**Definition**: Land/building held for rentals or capital appreciation (or both).
*Not for use in production/admin (PPE) or sale in ordinary course (Inventory).*

**Measurement**:
- Initial: Cost (including transaction costs)
- Subsequent:
  1. **Fair Value Model**: Changes in FV -> P&L (No depreciation)
  2. **Cost Model**: Cost - Acc Dep - Impairment (Same as PPE)

**Transfers**: Change in use required.`,
        keyTerms: ["investment property", "rentals", "capital appreciation", "fair value model"],
        commonTraps: [
            "Fair Value Model gains go to P&L (unlike Revaluation Model for PPE -> OCI)",
            "Property held for employees = PPE (PAS 16)",
            "Property held for sale = Inventory (PAS 2)"
        ],
        examTips: [
            "Check the INTENT: Rent/Appreciation vs Use vs Sale",
            "FV Model = No Depreciation"
        ],
        lastVerified: "2024-12-01"
    },

    // PAS 41 - Agriculture
    {
        topic: "FAR",
        subtopic: "Agriculture",
        standard: "PAS 41",
        content: `**PAS 41 Agriculture**

**Scope**: Biological assets, Agricultural produce at point of harvest.

**Measurement**:
- **Biological Assets**: Fair Value less Costs to Sell (FVLCTS) -> Change to P&L
- **Agricultural Produce**: FVLCTS at point of harvest -> Becomes Inventory cost

**Bearer Plants**: Treated as PPE (PAS 16) - e.g., mango trees, grape vines.
*Produce on bearer plants is PAS 41.*`,
        keyTerms: ["biological asset", "agricultural produce", "bearer plant", "FVLCTS"],
        commonTraps: [
            "Bearer plants are PPE, not Biological Assets",
            "FVLCTS changes go to P&L",
            "Land is PAS 16 or PAS 40, not PAS 41"
        ],
        examTips: [
            "Biological Asset -> FVLCTS",
            "Harvested Produce -> Inventory (Cost = FVLCTS at harvest)"
        ],
        lastVerified: "2024-12-01"
    },

    // PFRS 2 - Share-based Payment
    {
        topic: "FAR",
        subtopic: "Share-based Payment",
        standard: "PFRS 2",
        content: `**PFRS 2 Share-based Payment**

**Equity-settled**:
- Measure at FV of goods/services received OR FV of equity instruments granted (if services hard to value, e.g., employees)
- Measurement Date: Grant Date
- Entry: Expense/Asset Dr, Equity Cr
- No subsequent remeasurement

**Cash-settled** (e.g., SARs):
- Measure at FV of liability
- Remeasure at each reporting date -> P&L
- Entry: Expense Dr, Liability Cr`,
        keyTerms: ["equity-settled", "cash-settled", "grant date", "vesting conditions", "SARs"],
        commonTraps: [
            "Employees: Use FV of Equity Instruments at Grant Date",
            "Non-employees: Use FV of Goods/Services received",
            "Cash-settled liability is remeasured every year"
        ],
        examTips: [
            "Equity = Grant Date FV (Fixed)",
            "Liability = Current FV (Floating)"
        ],
        lastVerified: "2024-12-01"
    },

    // PFRS 5 - Non-current Assets Held for Sale
    {
        topic: "FAR",
        subtopic: "Non-current Assets Held for Sale and Discontinued Operations",
        standard: "PFRS 5",
        content: `**PFRS 5 Held for Sale**

**Criteria**:
- Available for immediate sale
- Sale is highly probable (Management commitment, active program, reasonable price, within 1 year)

**Measurement**:
- Lower of Carrying Amount and Fair Value less Costs to Sell
- **NO DEPRECIATION** while held for sale

**Discontinued Operation**:
- Component disposed of or held for sale
- Major line of business or geographical area
- Single amount on face of P&L (Net Income from Disc Ops)`,
        keyTerms: ["held for sale", "highly probable", "discontinued operation", "no depreciation"],
        commonTraps: [
            "Assets held for sale are NOT depreciated",
            "Write-down to FV less costs to sell is an impairment loss",
            "Abandonment is NOT held for sale"
        ],
        examTips: [
            "Check criteria for classification",
            "Stop depreciation immediately upon classification"
        ],
        lastVerified: "2024-12-01"
    },

    // PFRS 13 - Fair Value Measurement
    {
        topic: "FAR",
        subtopic: "Fair Value Measurement",
        standard: "PFRS 13",
        content: `**PFRS 13 Fair Value Measurement**

**Definition**: Price to sell an asset or transfer a liability in an orderly transaction between market participants at measurement date (Exit Price).

**Hierarchy**:
- **Level 1**: Quoted prices in active markets (unadjusted) - Most reliable
- **Level 2**: Observable inputs other than quoted prices
- **Level 3**: Unobservable inputs - Least reliable

**Valuation Techniques**: Market, Cost, Income approaches`,
        keyTerms: ["fair value", "exit price", "market participants", "level 1", "level 2", "level 3"],
        commonTraps: [
            "Transaction costs are NOT deducted from Fair Value (except for transport costs)",
            "Highest and best use applies to non-financial assets only"
        ],
        examTips: [
            "Level 1 is best",
            "Fair Value is an EXIT price"
        ],
        lastVerified: "2024-12-01"
    }
];

// ==================== ADVANCED FINANCIAL ACCOUNTING (AFAR) ====================

export const AFAR_KNOWLEDGE: KnowledgeEntry[] = [
    // Partnership Liquidation
    {
        topic: "AFAR",
        subtopic: "Partnership Liquidation - Lump Sum",
        content: `**Partnership Liquidation - Lump Sum Distribution**

**Process**:
1. Sell non-cash assets (record gain/loss)
2. Pay liquidation expenses
3. Pay external creditors (in full)
4. Distribute remaining cash to partners per capital balances

**Loss Absorption Priority**:
- Partners absorb losses based on P&L sharing ratio
- If partner's capital becomes negative (deficit):
  1. Partner contributes additional cash
  2. If unable, deficit allocated to remaining solvent partners
  3. Follow maximum loss absorption capacity

**Maximum Loss Absorption Capacity**:
\`\`\`
Capital Balance ÷ P&L Ratio = Maximum Loss Absorbable
\`\`\`
- Partner with highest ratio = most vulnerable (absorbs loss first)
- Used to determine cash priority schedule`,
        keyTerms: ["lump sum liquidation", "loss absorption", "deficit", "cash priority"],
        commonTraps: [
            "External creditors ALWAYS paid before partners",
            "Deficit from insolvent partner allocated by remaining partners' P&L ratio",
            "Right of offset: Partner loan receivable offsets capital deficit"
        ],
        examTips: [
            "Always check if partner has personal liability (unlimited liability)",
            "Calculate maximum loss absorption for cash priority problems",
            "Remember: Partnership debts > Partner capital"
        ],
        lastVerified: "2024-12-01"
    },

    // Partnership Liquidation - Installment
    {
        topic: "AFAR",
        subtopic: "Partnership Liquidation - Installment Method",
        content: `**Partnership Liquidation - Installment (Periodic) Distribution**

**Safe Payment Schedule Method**:
Assumes maximum possible loss at each distribution point.

**Steps**:
1. Calculate potential loss (remaining non-cash assets + liquidation expenses)
2. Allocate potential loss to partners per P&L ratio
3. Determine "safe" capital balances
4. Distribute available cash to partners with positive safe balances

**Cash Priority Program (Schedule of Safe Payments)**:
Shows sequence of distributions as cash becomes available.

**Calculation**:
\`\`\`
Safe Payment = Capital Balance - (Potential Loss × P&L Ratio) - Partner Loans Payable

If Safe Payment < 0, set to 0
\`\`\`

**Marshalling of Assets**:
- Partnership creditors have priority over partner's personal creditors for partnership assets
- Partner's personal creditors have priority over partnership creditors for partner's personal assets`,
        keyTerms: ["safe payment", "cash priority program", "marshalling", "installment liquidation"],
        commonTraps: [
            "Partner loans PAYABLE (to partners) are like capital for distribution purposes",
            "Partner loans RECEIVABLE (from partners) offset deficits",
            "Never distribute to partner with negative safe payment"
        ],
        examTips: [
            "Always assume MAXIMUM possible loss for safe payment calculation",
            "Check both capital and P&L ratio when determining payment priority",
            "Partner with highest (Capital/P&L Ratio) receives cash LAST"
        ],
        lastVerified: "2024-12-01"
    },

    // Consolidation
    {
        topic: "AFAR",
        subtopic: "Business Combinations and Consolidation",
        standard: "PFRS 3, PFRS 10",
        content: `**Business Combinations (PFRS 3) & Consolidation (PFRS 10)**

**Control Defined (PFRS 10)**:
1. Power over investee
2. Exposure to variable returns
3. Ability to use power to affect returns

**Acquisition Method (PFRS 3)**:
1. Identify acquirer
2. Determine acquisition date
3. Recognize and measure identifiable assets/liabilities at FAIR VALUE
4. Recognize and measure goodwill or bargain purchase gain

**Goodwill Calculation**:
\`\`\`
Full Goodwill Method:
Goodwill = (Consideration + NCI at FV) - Net Assets at FV

Partial Goodwill Method:
Goodwill = Consideration - Parent's Share of Net Assets at FV
NCI at proportionate share of Net Assets
\`\`\`

**Consolidation Eliminations (INVEST)**:
- **I**nvestment in subsidiary
- **N**CI allocation
- **V**aluation (fair value adjustments)
- **E**quity (subsidiary equity accounts)
- **S**ubsequent year adjustments
- **T**ransactions (intercompany)

**Intercompany Transactions**:
- **Downstream** (Parent to Sub): 100% eliminated, no NCI effect
- **Upstream** (Sub to Parent): Eliminate proportionately, affects NCI`,
        keyTerms: ["control", "acquisition method", "goodwill", "NCI", "downstream", "upstream", "elimination"],
        commonTraps: [
            "Fair value adjustments affect subsequent depreciation/amortization",
            "Goodwill is NOT amortized, only tested for impairment",
            "Upstream transactions reduce BOTH parent and NCI shares",
            "Investment in associate (20-50%) uses EQUITY method, not consolidation"
        ],
        examTips: [
            "Always start with full elimination, then allocate to Parent/NCI",
            "Remember: Control is power + returns + link between them",
            "Intercompany profits in inventory are fully eliminated regardless of direction"
        ],
        relatedTopics: ["PAS 28 Associates", "PAS 36 Impairment of Goodwill"],
        lastVerified: "2024-12-01"
    },

    // Foreign Currency
    {
        topic: "AFAR",
        subtopic: "Foreign Currency Transactions and Translation",
        standard: "PAS 21",
        content: `**PAS 21 Foreign Currency**

**Functional Currency**: Currency of primary economic environment

**Transaction vs Translation**:
- **Transaction**: Entity's own transactions in foreign currency
- **Translation**: Converting foreign operation's FS to presentation currency

**Monetary vs Non-Monetary Items**:

| Item Type | Rate Used | FX Gain/Loss |
|-----------|-----------|--------------|
| Monetary items | Closing rate | P&L |
| Non-monetary (at cost) | Historical rate | None |
| Non-monetary (at FV) | Rate at FV date | Follows underlying (OCI or P&L) |

**Translation of Foreign Operations**:
| Item | Rate |
|------|------|
| Assets & Liabilities | Closing rate |
| Income & Expenses | Average rate (or actual transaction rate) |
| Equity (historical) | Historical rate |

**Translation Difference → OCI (Cumulative Translation Adjustment)**

**Disposal of Foreign Operation**:
- Reclassify cumulative CTA from OCI to P&L`,
        keyTerms: ["functional currency", "presentation currency", "monetary", "closing rate", "historical rate", "CTA"],
        commonTraps: [
            "Non-monetary items at fair value use rate at DATE of fair value measurement",
            "Average rate is approximation; use transaction date rate if rates fluctuate significantly",
            "Partial disposal: proportionate reclassification of CTA",
            "Goodwill and FV adjustments of foreign subsidiary are in subsidiary's functional currency"
        ],
        examTips: [
            "Always identify: monetary vs non-monetary, cost vs fair value",
            "Translation differences go to OCI, transaction gains/losses go to P&L",
            "On disposal: CTA recycled (reclassified) to P&L"
        ],
        lastVerified: "2024-12-01"
    }
];

// ==================== TAXATION ====================

export const TAX_KNOWLEDGE: KnowledgeEntry[] = [
    // Corporate Income Tax
    {
        topic: "TAX",
        subtopic: "Corporate Income Tax (RCIT/MCIT)",
        law: "NIRC as amended by CREATE/EOPT",
        content: `**Corporate Income Tax Rates (CREATE Act - RA 11534, EOPT - RA 11976)**

**Regular Corporate Income Tax (RCIT)**:
- **25%** - Regular rate for domestic corporations and resident foreign corporations
- **20%** - Preferential rate for MSME (Micro, Small, Medium Enterprises)
  - Conditions: Net Taxable Income ≤ ₱5,000,000 AND Total Assets ≤ ₱100,000,000

**Minimum Corporate Income Tax (MCIT)**:
- **1%** (reduced from 2% under CREATE, effective July 1, 2020 to June 30, 2023)
- **2%** - Regular MCIT rate (restored July 1, 2023 onwards)
- **Basis**: Gross Income
- **When applicable**: Beginning 4th taxable year from start of operations
- **Imposed when**: MCIT > RCIT
- **Excess MCIT**: Creditable against RCIT for 3 succeeding years

**Gross Income for MCIT**:
\`\`\`
For Traders: Gross Sales - Sales Returns - Cost of Goods Sold
For Service: Gross Receipts - Sales Returns - Direct Costs of Service
\`\`\`

**Special Rates**:
- Proprietary educational institutions & hospitals (non-profit): 1% (until 2023), then 10%
- Regional Operating HQ: 10%
- Offshore Banking Units: 10% on onshore income`,
        keyTerms: ["RCIT", "MCIT", "MSME", "gross income", "CREATE Act", "EOPT"],
        commonTraps: [
            "MCIT uses GROSS income, not net taxable income",
            "MSME rate requires BOTH conditions (income AND assets)",
            "MCIT starts 4th year, not from registration",
            "Excess MCIT carried forward 3 years only, not indefinitely"
        ],
        examTips: [
            "Always compare RCIT vs MCIT and pay the HIGHER",
            "Remember MCIT basis = Gross Income (different from taxable income)",
            "MSME threshold amounts: ₱5M income AND ₱100M assets"
        ],
        lastVerified: "2024-12-01"
    },

    // VAT
    {
        topic: "TAX",
        subtopic: "Value Added Tax (VAT)",
        law: "NIRC Sec 105-115, EOPT Act (RA 11976)",
        content: `**Value Added Tax (VAT) - EOPT Act Amendments**

**VAT Rate**: 12%

**Registration Threshold**: ₱3,000,000 gross sales/receipts

**VAT Computation**:
\`\`\`
VAT Payable = Output VAT - Input VAT

Output VAT = (Sales × 12%) or (Sales × 12/112) if VAT-inclusive
Input VAT = VAT paid on purchases/services
\`\`\`

**Input VAT Crediting (EOPT - MAJOR CHANGE)**:
- Input VAT claimable upon **INVOICE ISSUANCE**
- Payment to supplier **NOT required** for claiming input VAT
- Applies to BOTH goods AND services
- This is the "Invoice System" under EOPT

**Zero-Rated Sales** (0% VAT but Input VAT claimable):
- Export sales
- Services rendered to non-residents
- Sales to PEZA/freeport locators

**VAT-Exempt Sales** (No VAT, NO Input VAT claimable):
- Agricultural products (original state)
- Basic necessities
- Educational services
- Services of banks (under gross receipts tax)

**Withholding VAT**:
- Government agencies withhold 5% final VAT on purchases`,
        keyTerms: ["Output VAT", "Input VAT", "zero-rated", "VAT-exempt", "invoice system", "EOPT"],
        commonTraps: [
            "Zero-rated ≠ VAT-exempt (zero-rated claims input VAT, exempt cannot)",
            "Under EOPT: Input VAT on invoice, NOT payment (major change from before)",
            "Mixed transactions require proportional input VAT allocation",
            "VAT on importation is paid and THEN claimed as input VAT"
        ],
        examTips: [
            "EOPT Invoice System is heavily tested - Input VAT on invoice issuance",
            "Distinguish zero-rated (with input VAT) vs exempt (no input VAT)",
            "Government purchases: 5% final withholding VAT"
        ],
        lastVerified: "2024-12-01"
    },

    // Individual Income Tax
    {
        topic: "TAX",
        subtopic: "Individual Income Tax",
        law: "NIRC Sec 24, TRAIN Law (RA 10963)",
        content: `**Individual Income Tax Rates (TRAIN Law - RA 10963)**

**Graduated Tax Rates (2023 onwards)**:
| Taxable Income (Annual) | Tax Rate |
|------------------------|----------|
| Not over ₱250,000 | 0% |
| Over ₱250,000 to ₱400,000 | 15% of excess over ₱250,000 |
| Over ₱400,000 to ₱800,000 | ₱22,500 + 20% of excess over ₱400,000 |
| Over ₱800,000 to ₱2,000,000 | ₱102,500 + 25% of excess over ₱800,000 |
| Over ₱2,000,000 to ₱8,000,000 | ₱402,500 + 30% of excess over ₱2,000,000 |
| Over ₱8,000,000 | ₱2,202,500 + 35% of excess over ₱8,000,000 |

**8% Gross Income Tax Option**:
- For self-employed and professionals
- In lieu of graduated rates AND percentage tax
- Conditions: Gross sales/receipts ≤ ₱3,000,000
- Rate: 8% on gross sales/receipts exceeding ₱250,000

**Compensation Income**:
- Subject to withholding tax
- 13th month and bonuses exempt up to ₱90,000

**Passive Income (Final Tax)**:
- Interest from banks: 20%
- Dividends (domestic): 10%
- Royalties: 20% (10% for books and literary works)
- Capital gains on shares: 15%
- Capital gains on real property: 6%`,
        keyTerms: ["graduated rates", "8% option", "final tax", "withholding tax", "TRAIN Law"],
        commonTraps: [
            "8% option requires gross ≤ ₱3M (NOT net income)",
            "8% rate applies to gross EXCEEDING ₱250,000, not from zero",
            "13th month exempt only up to ₱90,000",
            "Mixed income earners: Compensation (graduated) + Business (8% or graduated)"
        ],
        examTips: [
            "Know the breakpoints: ₱250K, ₱400K, ₱800K, ₱2M, ₱8M",
            "8% option = 8% × (Gross - ₱250,000) OR 8% × Gross - ₱20,000",
            "Compare graduated vs 8% option to determine better choice"
        ],
        lastVerified: "2024-12-01"
    },

    // Withholding Taxes
    {
        topic: "TAX",
        subtopic: "Withholding Taxes",
        law: "NIRC, RR 2-98 as amended",
        content: `**Withholding Tax System**

**Expanded Withholding Tax (EWT)** - Creditable:
| Income Payment | Rate |
|----------------|------|
| Professional fees (individual) | 5%/10%/15% (based on income level) |
| Professional fees (corporation) | 10%/15% |
| Rental (real property) | 5% |
| Rental (personal property) | 5% |
| Commissions | 10%/15% |
| Interest on loans | 20% (individuals), 15% (corporations) |

**Final Withholding Tax (FWT)** - Final:
| Income | Rate |
|--------|------|
| Interest from bank deposits | 20% |
| Dividends (domestic) | 10% |
| Royalties | 20% (10% for books) |
| Prizes (>₱10,000) | 20% |
| PCSO/lotto winnings (>₱10,000) | 20% |
| Capital gains on shares (not traded) | 15% |

**Key Distinction**:
- **EWT**: Creditable against income tax due, excess is refundable
- **FWT**: Tax obligation is FINAL, not included in return computation

**Withholding on Compensation**:
- Based on tax tables using graduated rates
- Employer remits to BIR monthly`,
        keyTerms: ["EWT", "FWT", "creditable", "final tax", "withholding agent"],
        commonTraps: [
            "EWT is creditable, FWT is final - different treatment in return",
            "Professional fees rate depends on gross income level",
            "Interest from bank is FWT (20%), interest from loans is EWT",
            "Prizes/winnings ≤₱10,000 are exempt from FWT"
        ],
        examTips: [
            "Know which payments are creditable (EWT) vs final (FWT)",
            "FWT income is NOT added to taxable income",
            "EWT rates vary by income level - check the thresholds"
        ],
        lastVerified: "2024-12-01"
    }
];

// ==================== AUDITING ====================

export const AUD_KNOWLEDGE: KnowledgeEntry[] = [
    // Audit Risk
    {
        topic: "AUD",
        subtopic: "Audit Risk Model",
        standard: "PSA 200, PSA 315, PSA 330",
        content: `**Audit Risk Model**

**Audit Risk (AR)** = Risk that auditor expresses inappropriate opinion when FS are materially misstated

**Formula**:
\`\`\`
AR = IR × CR × DR

Where:
- IR = Inherent Risk (susceptibility to misstatement before controls)
- CR = Control Risk (risk that controls fail to prevent/detect misstatement)
- DR = Detection Risk (risk that auditor procedures fail to detect misstatement)
\`\`\`

**Key Relationships**:
- Auditor can only control **Detection Risk**
- IR and CR are assessed (not controlled)
- As IR × CR increases, DR must decrease (more testing)
- DR has inverse relationship with IR × CR

**Risk Assessment**:
- **Inherent Risk factors**: Complexity, judgment, related parties, unusual transactions
- **Control Risk factors**: Control environment, risk assessment, information systems, monitoring

**Risk of Material Misstatement (RMM)** = IR × CR
- Assessed at FS level and assertion level
- Higher RMM → More substantive procedures

**Audit Risk Acceptable Level**:
- Based on engagement circumstances
- Lower AR → More evidence required`,
        keyTerms: ["audit risk", "inherent risk", "control risk", "detection risk", "RMM"],
        commonTraps: [
            "Auditor ASSESSES IR and CR, does NOT control them",
            "Lower detection risk = MORE audit work needed",
            "IR and CR cannot be reduced to zero (always some risk exists)",
            "Audit risk is for material misstatements, not all misstatements"
        ],
        examTips: [
            "Remember: AR = IR × CR × DR, only DR is auditor-controlled",
            "Inverse relationship: Higher IR/CR → Lower DR needed → More testing",
            "RMM = IR × CR (risk that exists independent of audit)"
        ],
        lastVerified: "2024-12-01"
    },

    // Audit Evidence
    {
        topic: "AUD",
        subtopic: "Audit Evidence",
        standard: "PSA 500, PSA 501",
        content: `**Audit Evidence (PSA 500)**

**Sufficient Appropriate Audit Evidence**:
- **Sufficient** = Quantity (enough evidence)
- **Appropriate** = Quality (relevance + reliability)

**Reliability Hierarchy** (most to least reliable):
1. External evidence from independent sources
2. Internally generated evidence with effective controls
3. Evidence obtained directly by auditor > from entity
4. Documentary evidence > oral representations
5. Original documents > copies

**Audit Procedures**:
| Procedure | Description | Primary Assertion |
|-----------|-------------|-------------------|
| Inspection | Examine records/documents | Existence, Rights |
| Observation | Watch process/procedure | Occurrence |
| External Confirmation | Third party written response | Existence, Rights, Completeness |
| Recalculation | Check mathematical accuracy | Accuracy |
| Reperformance | Execute controls/procedures | Controls |
| Analytical Procedures | Evaluate relationships | Reasonableness |
| Inquiry | Seek information from entity | All (corroborative) |

**Direction of Testing**:
- **Trace** (Source → Books): Tests **Completeness** (understatement)
- **Vouch** (Books → Source): Tests **Existence/Occurrence** (overstatement)`,
        keyTerms: ["sufficient", "appropriate", "relevance", "reliability", "trace", "vouch", "confirmation"],
        commonTraps: [
            "Inquiry ALONE is not sufficient - needs corroboration",
            "External confirmation is strong but not absolute proof",
            "Trace tests completeness (not recorded), Vouch tests existence (not real)",
            "Observation only evidences moment in time"
        ],
        examTips: [
            "Remember reliability hierarchy: External > Internal, Direct > Indirect",
            "Match procedure to assertion being tested",
            "Trace = Forward (source to records), Vouch = Backward (records to source)"
        ],
        lastVerified: "2024-12-01"
    },

    // Audit Opinions
    {
        topic: "AUD",
        subtopic: "Auditor's Report and Opinions",
        standard: "PSA 700, PSA 705, PSA 706",
        content: `**Types of Audit Opinions (PSA 700, 705)**

**Decision Framework**:
| FS Materially Misstated? | Nature | Materiality Level | Opinion |
|--------------------------|--------|-------------------|---------|
| No | - | - | **Unmodified** |
| Yes | Misstatement | Material but not pervasive | **Qualified** |
| Yes | Misstatement | Material and pervasive | **Adverse** |
| Unable to obtain evidence | Limitation | Material but not pervasive | **Qualified** |
| Unable to obtain evidence | Limitation | Material and pervasive | **Disclaimer** |

**Key Definitions**:
- **Pervasive**: Effects are spread throughout FS, or represent substantial portion, or are fundamental to users' understanding

**Unmodified Opinion**:
- FS prepared in accordance with applicable framework
- Free from material misstatement

**Modified Opinions**:
- **Qualified**: "Except for" language
- **Adverse**: "FS do not present fairly"
- **Disclaimer**: "We do not express an opinion"

**Other Report Modifications (PSA 706)**:
- **Emphasis of Matter**: Draw attention to matter properly presented (going concern, subsequent events)
- **Other Matter**: Relevant to understanding of audit/responsibilities

**Key Audit Matters (KAM) - PSA 701**:
- Required for listed entities
- Matters of most significance in audit
- Communicated with those charged with governance`,
        keyTerms: ["unmodified", "qualified", "adverse", "disclaimer", "pervasive", "KAM", "EOM"],
        commonTraps: [
            "Pervasive = widespread OR fundamental, not just 'very material'",
            "Scope limitation → Qualified OR Disclaimer (depends on pervasiveness)",
            "Material misstatement → Qualified OR Adverse (depends on pervasiveness)",
            "EOM does NOT affect opinion; it's additional information"
        ],
        examTips: [
            "Use 2x2 matrix: Misstatement vs Limitation × Material vs Pervasive",
            "Disclaimer = Cannot form opinion (total scope limitation)",
            "Adverse = Can form opinion but FS are materially wrong"
        ],
        lastVerified: "2024-12-01"
    }
];

// ==================== MANAGEMENT ADVISORY SERVICES (MAS) ====================

export const MAS_KNOWLEDGE: KnowledgeEntry[] = [
    // CVP Analysis
    {
        topic: "MAS",
        subtopic: "Cost-Volume-Profit Analysis",
        content: `**CVP Analysis Formulas**

**Basic CVP Equation**:
\`\`\`
Sales - Variable Costs - Fixed Costs = Net Income
SP(x) - VC(x) - FC = NI
\`\`\`

**Contribution Margin**:
\`\`\`
CM per unit = SP - VC per unit
CM Ratio = CM / SP = (SP - VC) / SP
\`\`\`

**Break-Even Point**:
\`\`\`
BEP (units) = Fixed Costs / CM per unit
BEP (pesos) = Fixed Costs / CM Ratio
\`\`\`

**Target Profit**:
\`\`\`
Units = (Fixed Costs + Target Profit) / CM per unit
Pesos = (Fixed Costs + Target Profit) / CM Ratio
\`\`\`

**Target AFTER-TAX Profit**:
\`\`\`
Before-tax profit needed = After-tax profit / (1 - Tax Rate)
Units = (FC + Before-tax target) / CM per unit
\`\`\`

**Margin of Safety**:
\`\`\`
MOS (units) = Actual Sales - BEP Sales
MOS (pesos) = Actual Sales - BEP Sales (in pesos)
MOS Ratio = MOS / Actual Sales
\`\`\`

**Operating Leverage**:
\`\`\`
Degree of Operating Leverage = CM / Net Income
% Change in NI = DOL × % Change in Sales
\`\`\``,
        keyTerms: ["BEP", "contribution margin", "CM ratio", "MOS", "operating leverage", "target profit"],
        commonTraps: [
            "After-tax target must be converted to before-tax first",
            "MOS uses SALES figures, not units (or specify which)",
            "DOL changes at different sales levels",
            "Mixed costs must be separated into fixed and variable"
        ],
        examTips: [
            "Always identify: SP, VC per unit, Total FC, Tax rate if applicable",
            "For after-tax: Divide target by (1 - tax rate) to get before-tax",
            "High DOL = High risk but high reward from sales increases"
        ],
        lastVerified: "2024-12-01"
    },

    // Variance Analysis
    {
        topic: "MAS",
        subtopic: "Standard Costing and Variance Analysis",
        content: `**Variance Analysis Formulas**

**Material Variances**:
\`\`\`
Material Price Variance (MPV):
= (Actual Price - Standard Price) × Actual Quantity Purchased
= (AP - SP) × AQ

Material Quantity/Usage Variance (MQV):
= (Actual Quantity Used - Standard Quantity Allowed) × Standard Price
= (AQ used - SQ) × SP

Where: SQ = Standard Qty per unit × Actual units produced
\`\`\`

**Labor Variances**:
\`\`\`
Labor Rate Variance (LRV):
= (Actual Rate - Standard Rate) × Actual Hours
= (AR - SR) × AH

Labor Efficiency Variance (LEV):
= (Actual Hours - Standard Hours Allowed) × Standard Rate
= (AH - SH) × SR

Where: SH = Standard Hours per unit × Actual units produced
\`\`\`

**Variable Overhead Variances**:
\`\`\`
VOH Spending Variance:
= (Actual VOH) - (Actual Hours × Standard VOH Rate)
= Actual VOH - (AH × SR)

VOH Efficiency Variance:
= (Actual Hours - Standard Hours) × Standard VOH Rate
= (AH - SH) × SR
\`\`\`

**Fixed Overhead Variances (2-Way)**:
\`\`\`
Budget Variance = Actual FOH - Budgeted FOH
Volume Variance = (Budgeted Hours - Standard Hours) × FOH Rate
              = (Normal Capacity - Actual Production) × FOH Rate
\`\`\`

**Interpretation**:
- Favorable (F): Actual < Standard (cost saving)
- Unfavorable (U): Actual > Standard (cost overrun)`,
        keyTerms: ["MPV", "MQV", "LRV", "LEV", "spending variance", "efficiency variance", "volume variance"],
        commonTraps: [
            "MPV uses AQ PURCHASED, MQV uses AQ USED",
            "Standard quantity is based on ACTUAL output, not budgeted",
            "FOH Volume variance relates to capacity utilization, not efficiency",
            "Price variance is purchasing responsibility; Quantity is production responsibility"
        ],
        examTips: [
            "Always compute: SQ = Std × Actual output, SH = Std × Actual output",
            "Price variances use ACTUAL quantity, Quantity variances use STANDARD price",
            "Favorable = Actual < Standard (saved money)"
        ],
        lastVerified: "2024-12-01"
    },

    // Capital Budgeting
    {
        topic: "MAS",
        subtopic: "Capital Budgeting",
        content: `**Capital Budgeting Decision Methods**

**Net Present Value (NPV)**:
\`\`\`
NPV = Σ [Cash Flow_t / (1 + r)^t] - Initial Investment

Decision: Accept if NPV > 0
\`\`\`
- Measures absolute value creation
- Uses required rate of return (WACC or cost of capital)
- Preferred method for mutually exclusive projects

**Internal Rate of Return (IRR)**:
\`\`\`
NPV = 0 when discount rate = IRR

Decision: Accept if IRR > Required Rate of Return
\`\`\`
- Rate that makes NPV = 0
- Interpolation formula: IRR = Lower rate + (NPV at lower / (NPV lower - NPV upper)) × (Upper - Lower)

**Profitability Index (PI)**:
\`\`\`
PI = PV of Cash Inflows / Initial Investment
   = (NPV + Initial Investment) / Initial Investment

Decision: Accept if PI > 1
\`\`\`
- Useful for capital rationing
- Measures relative profitability

**Payback Period**:
\`\`\`
Payback = Years before full recovery + (Unrecovered cost / Cash flow in recovery year)

Decision: Accept if Payback < Target Period
\`\`\`
- Ignores time value of money
- Ignores cash flows after payback

**Discounted Payback**:
- Same as payback but uses discounted cash flows
- Accounts for time value but still ignores post-payback flows`,
        keyTerms: ["NPV", "IRR", "PI", "payback", "WACC", "capital rationing"],
        commonTraps: [
            "NPV vs IRR conflict: NPV is preferred for mutually exclusive projects",
            "IRR may give multiple rates for non-conventional cash flows",
            "Payback ignores time value AND cash flows after payback",
            "PI is best for capital rationing, NPV for value maximization"
        ],
        examTips: [
            "NPV = Wealth maximization (absolute), PI = Bang for buck (relative)",
            "IRR conflicts with NPV when projects have different scales/timing",
            "Payback is simplest but least reliable method"
        ],
        lastVerified: "2024-12-01"
    }
];

// ==================== REGULATORY FRAMEWORK (RFBT) ====================

export const RFBT_KNOWLEDGE: KnowledgeEntry[] = [
    // Obligations
    {
        topic: "RFBT",
        subtopic: "Obligations and Contracts",
        law: "Civil Code of the Philippines",
        content: `**Obligations (Civil Code Art. 1156-1304)**

**Definition**: Juridical necessity to give, to do, or not to do

**Sources of Obligations (Art. 1157)**:
1. **Law** - Imposed by law
2. **Contracts** - Meeting of minds
3. **Quasi-contracts** - Lawful, voluntary, unilateral acts
4. **Delicts** - Acts or omissions punished by law
5. **Quasi-delicts** - Fault or negligence without privity

**Classification of Obligations**:

**By Subject Matter**:
- Determinate (specific thing)
- Generic (class of things)
- Alternative (choice among several)
- Facultative (one principal, substitute allowed)

**By Number of Parties**:
- Joint (proportionate liability)
- Solidary (whole obligation from any debtor)

**Extinguishment of Obligations (Art. 1231)**:
1. **Payment** - Performance of obligation
2. **Loss of thing due** - Without fault of debtor
3. **Condonation** - Gratuitous remission
4. **Confusion** - Merger of debtor and creditor
5. **Compensation** - Mutual debts set off
6. **Novation** - Substitution of obligation

**Special Terms**:
- **Solutio Indebiti**: Payment of what is not due (must return)
- **Negotiorum Gestio**: Voluntary management of another's affairs`,
        keyTerms: ["obligation", "solidary", "joint", "novation", "compensation", "solutio indebiti"],
        commonTraps: [
            "Solidary obligation: Each debtor liable for WHOLE debt",
            "Joint obligation: Each debtor liable for SHARE only",
            "Compensation requires reciprocal creditor-debtor relationship",
            "Novation extinguishes old obligation - new one arises"
        ],
        examTips: [
            "Remember sources: LCQQQ (Law, Contract, Quasi-contract, Quasi-delict, Delict)",
            "Solidary needs express stipulation or provided by law",
            "Loss of thing extinguishes obligation only if without debtor's fault"
        ],
        lastVerified: "2024-12-01"
    },

    // Contracts
    {
        topic: "RFBT",
        subtopic: "Contracts",
        law: "Civil Code Art. 1305-1422",
        content: `**Contracts (Civil Code Art. 1305-1422)**

**Definition**: Meeting of minds between two persons whereby one binds himself to give something or render service

**Essential Requisites (Art. 1318)**:
1. **Consent** - Meeting of minds
2. **Object** - Subject matter (determinate, licit)
3. **Cause** - Consideration

**Form of Contracts (Art. 1356)**:
- Generally, contracts are valid in ANY form
- **Form required for validity**: Donation of real property, contracts of antichresis
- **Form required for enforceability**: Sale of land (Statute of Frauds)
- **Form required for convenience**: Public documents for recording

**Defective Contracts**:

| Type | Defect | Effect | Remedy |
|------|--------|--------|--------|
| **Rescissible** | Economic prejudice | Valid until rescinded | Rescission (4 years) |
| **Voidable** | Vitiated consent | Valid until annulled | Annulment (4 years) |
| **Unenforceable** | Form/authority lacking | Cannot be sued upon | Ratification |
| **Void/Inexistent** | No essential element | No effect from start | Cannot be ratified |

**Vices of Consent**:
- Mistake (error)
- Violence (force)
- Intimidation (threat)
- Undue Influence
- Fraud (dolo)

**Void Contracts (Art. 1409)**:
- Absolutely simulated
- Cause is contrary to law, morals, public order
- Object is outside commerce
- Impossible service
- Intention not ascertainable`,
        keyTerms: ["consent", "object", "cause", "rescissible", "voidable", "unenforceable", "void"],
        commonTraps: [
            "Voidable is VALID until annulled; Void is NEVER valid",
            "Rescissible contracts involve NO vice of consent",
            "Statute of Frauds: Requires written evidence for land sales",
            "4-year prescription for rescission AND annulment"
        ],
        examTips: [
            "Remember hierarchy: Rescissible → Voidable → Unenforceable → Void",
            "Vice of consent = Voidable, not void",
            "Void contracts cannot be ratified; Unenforceable CAN be"
        ],
        lastVerified: "2024-12-01"
    },

    // Corporation Law
    {
        topic: "RFBT",
        subtopic: "Corporation Law",
        law: "Revised Corporation Code (RA 11232)",
        content: `**Revised Corporation Code (RA 11232) - Key Provisions**

**Corporation Defined**: Artificial being created by law, having:
- Right of succession
- Powers, attributes, properties as authorized

**Major Changes from Old Code**:
1. **Perpetual Existence** (Default) - No more 50-year limit
2. **One Person Corporation (OPC)** - Single stockholder allowed
3. **No Minimum Capital** - Unless required by special law
4. **Electronic Filing** - Allowed for SEC submissions

**Corporate Powers (Sec. 35)**:
- Sue and be sued
- Adopt and use corporate seal
- Amend articles of incorporation
- Enter contracts, acquire property
- Conduct business

**Board of Directors**:
- Minimum: 5 directors (15 maximum)
- Residency: Majority must be Philippine residents
- Term: 1 year (until successors elected)
- Compensation: Reasonable per diems

**Stockholders' Rights**:
- Voting rights
- Dividend rights
- Pre-emptive rights (unless denied in AOI)
- Appraisal right
- Inspection right

**Piercing the Corporate Veil**:
Applied when corporation is used to:
- Defeat public convenience
- Justify wrong
- Protect fraud
- Defend crime
Tests: Alter ego, instrumentality, fraud

**Corporate Recovery and Liquidation**:
- Governed by FRIA (RA 10142)
- Rehabilitation vs Liquidation
- Stay order during rehabilitation`,
        keyTerms: ["perpetual existence", "OPC", "piercing the veil", "pre-emptive right", "appraisal right"],
        commonTraps: [
            "Perpetual existence is DEFAULT under RCC, not maximum 50 years",
            "OPC is separate juridical personality, not sole proprietorship",
            "Pre-emptive right can be denied in AOI",
            "Piercing the veil is exception, not rule"
        ],
        examTips: [
            "Know major RCC changes from old Corporation Code",
            "Remember the 3 tests for piercing: Alter ego, Instrumentality, Fraud",
            "Board: 5-15 directors, majority residents, 1-year term"
        ],
        lastVerified: "2024-12-01"
    }
];

// ==================== STANDARD REFERENCES ====================

export const PFRS_STANDARDS: StandardReference[] = [
    {
        code: "PFRS 9",
        title: "Financial Instruments",
        effectiveDate: "2018-01-01",
        keyParagraphs: {
            "4.1.1": "Classification based on business model and cash flow characteristics",
            "4.1.2": "Amortized cost category requirements",
            "4.1.2A": "FVOCI category requirements",
            "5.5.1": "Expected credit loss model introduction",
            "5.5.15": "Simplified approach for trade receivables",
            "B4.1.7-B4.1.26": "SPPI (Solely Payments of Principal and Interest) test guidance"
        },
        amendments: ["Annual Improvements 2018-2020 Cycle"]
    },
    {
        code: "PFRS 15",
        title: "Revenue from Contracts with Customers",
        effectiveDate: "2018-01-01",
        keyParagraphs: {
            "9": "Step 1 - Identify contract criteria",
            "22": "Step 2 - Performance obligation identification",
            "27": "Distinct goods/services definition",
            "47": "Step 3 - Transaction price determination",
            "73": "Step 4 - Allocation to performance obligations",
            "31": "Step 5 - Satisfaction of performance obligations",
            "35": "Over time recognition criteria",
            "38": "Point in time indicators"
        }
    },
    {
        code: "PFRS 16",
        title: "Leases",
        effectiveDate: "2019-01-01",
        keyParagraphs: {
            "9": "Definition of a lease",
            "22": "Lessee recognition exemptions",
            "23": "Right-of-use asset initial measurement",
            "26": "Lease liability initial measurement",
            "29": "ROU asset subsequent measurement",
            "36": "Lease liability subsequent measurement",
            "61": "Lessor classification criteria"
        }
    },
    {
        code: "PAS 2",
        title: "Inventories",
        effectiveDate: "2005-01-01",
        keyParagraphs: {
            "9": "Measurement at lower of cost and NRV",
            "10": "Cost of inventories components",
            "25": "Cost formulas (FIFO, Weighted Average)",
            "28": "NRV write-down recognition",
            "33": "Reversal of write-down"
        }
    },
    {
        code: "PAS 16",
        title: "Property, Plant and Equipment",
        effectiveDate: "2005-01-01",
        keyParagraphs: {
            "7": "Recognition criteria",
            "15": "Initial measurement at cost",
            "16": "Cost components",
            "29": "Cost model",
            "31": "Revaluation model",
            "43": "Depreciation - depreciable amount",
            "50": "Depreciation period and method review"
        }
    },
    {
        code: "PAS 21",
        title: "The Effects of Changes in Foreign Exchange Rates",
        effectiveDate: "2005-01-01",
        keyParagraphs: {
            "8": "Functional currency definition",
            "21": "Reporting foreign currency transactions",
            "23": "Monetary vs non-monetary items",
            "28": "Exchange differences recognition",
            "39": "Translation to presentation currency"
        }
    },
    {
        code: "PAS 36",
        title: "Impairment of Assets",
        effectiveDate: "2004-03-31",
        keyParagraphs: {
            "6": "Recoverable amount definition",
            "9": "Identifying asset that may be impaired",
            "18": "Fair value less costs of disposal",
            "30": "Value in use calculation",
            "59": "Recognition of impairment loss",
            "110": "Reversal of impairment loss"
        }
    }
];

// ==================== TAX RATE TABLES ====================

export const TAX_RATES: TaxRate[] = [
    // Corporate
    { type: "RCIT", rate: "25%", basis: "Net Taxable Income", effectiveDate: "2020-07-01", source: "NIRC Sec 27(A), CREATE Act" },
    { type: "RCIT-MSME", rate: "20%", basis: "Net Taxable Income", conditions: "TI ≤ ₱5M AND Assets ≤ ₱100M", effectiveDate: "2020-07-01", source: "NIRC Sec 27(A), CREATE Act" },
    { type: "MCIT", rate: "2%", basis: "Gross Income", conditions: "Starting 4th taxable year", effectiveDate: "2023-07-01", source: "NIRC Sec 27(E)" },

    // VAT
    { type: "VAT", rate: "12%", basis: "Gross Selling Price/Gross Receipts", effectiveDate: "2006-02-01", source: "NIRC Sec 106" },
    { type: "VAT Registration Threshold", rate: "₱3,000,000", basis: "Annual Gross Sales/Receipts", effectiveDate: "2018-01-01", source: "NIRC Sec 109, TRAIN Law" },

    // Final Withholding
    { type: "FWT - Bank Interest", rate: "20%", basis: "Interest Income", effectiveDate: "1998-01-01", source: "NIRC Sec 24(B)(1)" },
    { type: "FWT - Dividends (Domestic)", rate: "10%", basis: "Dividend Income", effectiveDate: "2018-01-01", source: "NIRC Sec 24(B)(2), TRAIN Law" },
    { type: "FWT - Royalties", rate: "20%", basis: "Royalty Income", conditions: "10% for books/literary works", effectiveDate: "1998-01-01", source: "NIRC Sec 24(B)(1)" },

    // Capital Gains
    { type: "CGT - Real Property", rate: "6%", basis: "Gross Selling Price or FMV, whichever is higher", effectiveDate: "2018-01-01", source: "NIRC Sec 24(D), TRAIN Law" },
    { type: "CGT - Shares (Not Listed)", rate: "15%", basis: "Net Capital Gains", effectiveDate: "2018-01-01", source: "NIRC Sec 24(C), TRAIN Law" }
];

// ==================== VERIFICATION HELPERS ====================

/**
 * Verify if a standard citation exists
 */
export function verifyStandardCitation(citation: string): boolean {
    const standardPattern = /^(PFRS|PAS|PSA|IAS|IFRS)\s*\d+(\.\d+)?$/i;
    if (!standardPattern.test(citation)) return false;

    const [code] = citation.split(/[\.\s]/);
    return PFRS_STANDARDS.some(s => s.code.toUpperCase() === code.toUpperCase());
}

/**
 * Get knowledge entry by topic and subtopic
 */
export function getKnowledgeEntry(topic: string, subtopic: string): KnowledgeEntry | undefined {
    const allKnowledge = [
        ...FAR_KNOWLEDGE,
        ...AFAR_KNOWLEDGE,
        ...TAX_KNOWLEDGE,
        ...AUD_KNOWLEDGE,
        ...MAS_KNOWLEDGE,
        ...RFBT_KNOWLEDGE
    ];

    return allKnowledge.find(k =>
        k.topic.toUpperCase() === topic.toUpperCase() &&
        k.subtopic.toLowerCase().includes(subtopic.toLowerCase())
    );
}

/**
 * Search knowledge base
 */
export function searchKnowledge(query: string): KnowledgeEntry[] {
    const lowerQuery = query.toLowerCase();
    const allKnowledge = [
        ...FAR_KNOWLEDGE,
        ...AFAR_KNOWLEDGE,
        ...TAX_KNOWLEDGE,
        ...AUD_KNOWLEDGE,
        ...MAS_KNOWLEDGE,
        ...RFBT_KNOWLEDGE
    ];

    return allKnowledge.filter(k =>
        k.topic.toLowerCase().includes(lowerQuery) ||
        k.subtopic.toLowerCase().includes(lowerQuery) ||
        k.content.toLowerCase().includes(lowerQuery) ||
        k.keyTerms.some(t => t.toLowerCase().includes(lowerQuery))
    );
}

/**
 * Get exam traps for a topic
 */
export function getExamTraps(topic: string): string[] {
    const knowledge = getKnowledgeEntry(topic, "");
    if (!knowledge) return [];
    return knowledge.commonTraps || [];
}

/**
 * Get knowledge entries by topic
 */
export function getKnowledgeByTopic(topic: string): KnowledgeEntry[] {
    const lowerTopic = topic.toLowerCase();
    const allKnowledge = [
        ...FAR_KNOWLEDGE,
        ...AFAR_KNOWLEDGE,
        ...TAX_KNOWLEDGE,
        ...AUD_KNOWLEDGE,
        ...MAS_KNOWLEDGE,
        ...RFBT_KNOWLEDGE,
    ];
    return allKnowledge.filter(k => k.topic.toLowerCase() === lowerTopic);
}

/**
 * Get a standard reference by code
 */
export function getStandardReference(code: string): StandardReference | undefined {
    return PFRS_STANDARDS.find(s => s.code.toLowerCase() === code.toLowerCase());
}

// Export all knowledge
export const CPALE_KNOWLEDGE = {
    FAR: FAR_KNOWLEDGE,
    AFAR: AFAR_KNOWLEDGE,
    TAX: TAX_KNOWLEDGE,
    AUD: AUD_KNOWLEDGE,
    MAS: MAS_KNOWLEDGE,
    RFBT: RFBT_KNOWLEDGE,
    STANDARDS: PFRS_STANDARDS,
    TAX_RATES
};

export default CPALE_KNOWLEDGE;

